import { appraisifyPDFTheme } from '@aloft/toolkit-sui';
import { Image, Page, Text, View } from '@react-pdf/renderer';

import logo from '../../../assets/logos/appraisify/appraisify-blue.png';
import { InspectionCheckedIcon } from '../components/InspectionCheckedIcon.tsx';
import { fontStyles, pageStyles, coverPageStyles as styles } from '../styles.ts';
import { CoverPageProps } from '../types.ts';

const { colors } = appraisifyPDFTheme;
const INTERNAL_VALUATION_BACKGROUND_COLOR = '#182530';

export const CoverPage = ({
  subjectAddress,
  opinionOfMarketValue,
  totalCostToCure,
  subjectPropertyImage,
  intendedUsers,
  effectiveDate,
  shortPropertyType,
  inspectorType,
  appraisalType,
  includeTotalCostToCure,
  includeTotalRenovationBudget,
  includeOpinionOfAfterRepairValue,
  hasArvOption,
  totalRenovationBudget,
  opinionOfAfterRepairValue,
  estimatedROI,
  clientName,
  inspectionDate,
  valueType,
  inspectionData,
  appraisalReportDisclaimer,
  isInternalValuation,
  clientLogoUrl,
}: CoverPageProps) => (
  <Page size="A4" style={pageStyles.page}>
    <View
      style={{
        ...styles.page,
        backgroundColor: isInternalValuation
          ? INTERNAL_VALUATION_BACKGROUND_COLOR
          : colors.backgroundAccent,
      }}
    >
      <View>
        <View style={styles.header}>
          <View style={styles.headerRow}>
            <Image src={clientLogoUrl || logo} style={styles.headerLogo} />

            {!isInternalValuation && (
              <Text style={styles.headerProductTypeTag}>{appraisalType}</Text>
            )}
          </View>
          {!isInternalValuation && (
            <View style={styles.inspectionCheckboxes}>
              <View style={styles.inspectionCheckboxSection}>
                {inspectionData?.hasInspection ? (
                  <InspectionCheckedIcon />
                ) : (
                  <Text style={styles.inspectionCheckboxUnchecked} />
                )}
                <Text style={styles.inspectionCheckboxText}>Inspection</Text>
              </View>
              <View style={styles.inspectionCheckboxSection}>
                {inspectionData?.hasInspection ? (
                  <Text style={styles.inspectionCheckboxUnchecked} />
                ) : (
                  <InspectionCheckedIcon />
                )}
                <Text style={styles.inspectionCheckboxText}>No Inspection</Text>
              </View>
            </View>
          )}
        </View>

        <View style={{ margin: 56 }}>
          {isInternalValuation ? (
            <>
              <Text style={fontStyles.h1}>Internal</Text>
              <Text style={fontStyles.h1}>Valuation</Text>
            </>
          ) : (
            <>
              <Text style={fontStyles.h1}>Appraisal</Text>
              <Text style={fontStyles.h1}>Report</Text>
            </>
          )}
        </View>
      </View>

      <View style={{ flexDirection: 'row' }}>
        {subjectPropertyImage ? (
          <Image
            src={subjectPropertyImage}
            style={[styles.subjectPropertyImage, { height: hasArvOption ? 305 : 250 }]}
          />
        ) : (
          <View
            style={{ width: styles.subjectPropertyImage.width, height: hasArvOption ? 305 : 250 }}
          />
        )}

        <View style={styles.details}>
          <View style={styles.detail}>
            <Text style={[fontStyles.bodyRegularLight, fontStyles.uppercase]}>
              Property Location
            </Text>
            <Text style={fontStyles.bodyBold}>{subjectAddress}</Text>
          </View>
          {hasArvOption ? (
            <>
              {includeTotalRenovationBudget && (
                <View style={styles.detail}>
                  <Text style={[fontStyles.bodyRegularLight, fontStyles.uppercase]}>
                    Renovation Budget
                  </Text>
                  <Text style={fontStyles.bodyBold}>{totalRenovationBudget}</Text>
                </View>
              )}
              <View style={styles.detail}>
                <Text style={[fontStyles.bodyRegularLight, fontStyles.uppercase]}>
                  Opinion of As-Is Value
                </Text>
                <Text style={fontStyles.bodyBold}>{opinionOfMarketValue}</Text>
              </View>
              {includeOpinionOfAfterRepairValue && (
                <View style={styles.detail}>
                  <Text style={[fontStyles.bodyRegularLight, fontStyles.uppercase]}>
                    Opinion of After-Repair Value
                  </Text>
                  <Text style={fontStyles.bodyBold}>{opinionOfAfterRepairValue}</Text>
                </View>
              )}
              {includeOpinionOfAfterRepairValue && (
                <View style={styles.detail}>
                  <Text style={[fontStyles.bodyRegularLight, fontStyles.uppercase]}>
                    Estimated Return on Investment
                  </Text>
                  <Text style={fontStyles.bodyBold}>{estimatedROI}</Text>
                </View>
              )}
              {clientName && (
                <View style={styles.detail}>
                  <Text style={[fontStyles.bodyRegularLight, fontStyles.uppercase]}>
                    Client Name
                  </Text>
                  <Text style={fontStyles.bodyBold}>{clientName}</Text>
                </View>
              )}
            </>
          ) : (
            <>
              <View style={styles.detail}>
                <Text style={[fontStyles.bodyRegularLight, fontStyles.uppercase]}>
                  Opinion of Market Value
                </Text>
                <Text style={fontStyles.bodyBold}>{opinionOfMarketValue}</Text>
              </View>
              {includeTotalCostToCure && (
                <View style={styles.detail}>
                  <Text style={[fontStyles.bodyRegularLight, fontStyles.uppercase]}>
                    Total Cost to Cure
                  </Text>
                  <Text style={fontStyles.bodyBold}>{totalCostToCure}</Text>
                </View>
              )}
              {intendedUsers && (
                <View style={styles.detail}>
                  <Text style={[fontStyles.bodyRegularLight, fontStyles.uppercase]}>
                    Intended User(s)
                  </Text>
                  <Text style={fontStyles.bodyBold}>{intendedUsers}</Text>
                </View>
              )}
            </>
          )}
          <View style={styles.detail}>
            <Text style={[fontStyles.bodyRegularLight, fontStyles.uppercase]}>Effective Date</Text>
            <Text style={fontStyles.bodyBold}>{effectiveDate}</Text>
          </View>
          {inspectionDate && (
            <View style={styles.detail}>
              <Text style={[fontStyles.bodyRegularLight, fontStyles.uppercase]}>
                Inspection Date
              </Text>
              <Text style={fontStyles.bodyBold}>{inspectionDate}</Text>
            </View>
          )}
        </View>
      </View>

      {!isInternalValuation && (
        <View style={styles.tags}>
          {appraisalType.length > 0 && (
            <View style={styles.tag}>
              <Text>Property Type</Text>
              <Text style={styles.tagValue}>{shortPropertyType}</Text>
            </View>
          )}
          {inspectorType.length > 0 && (
            <View style={styles.tag}>
              <Text>Inspector Type</Text>
              <Text style={styles.tagValue}>{inspectorType}</Text>
            </View>
          )}
          {valueType.length > 0 && (
            <View style={styles.tag}>
              <Text>Valuation Type</Text>
              <Text style={styles.tagValue}>{valueType}</Text>
            </View>
          )}
        </View>
      )}
      {appraisalReportDisclaimer && (
        <View style={styles.disclaimer}>
          <Text style={[fontStyles.bodyItalic, fontStyles.accent]}>
            {appraisalReportDisclaimer}
          </Text>
        </View>
      )}
    </View>
  </Page>
);
